/**
 * iter211 — Sessions tab view preferences with per-group localStorage persistence.
 *
 * Persists the admin's choice of tab, range, view mode, hide-cancelled toggle,
 * group-by-month accordion, and pagination across reloads. Key is scoped per
 * group so each group remembers its own preferred view.
 */
import { useEffect, useState, useCallback } from 'react';

const DEFAULTS = {
  tab: 'upcoming',          // 'upcoming' | 'past' | 'all'
  hideCancelled: true,
  quickRange: 'this_week',  // iter236 — default view: this week's sessions
  displayMode: 'list',      // 'list' | 'calendar'
  groupByMonth: true,
  pageSize: 50,
};

// iter236 — v2: key bumped so the new "This week" default applies to everyone
// once (old saved views often had quickRange:'all' persisted incidentally).
const storageKey = (groupId) => `splitease.sessions_view.v2.${groupId || 'global'}`;

export default function useSessionsView(groupId) {
  // Lazy initial: read once from localStorage on mount.
  const [view, setViewState] = useState(() => {
    if (typeof window === 'undefined') return { ...DEFAULTS };
    try {
      const raw = window.localStorage.getItem(storageKey(groupId));
      if (!raw) return { ...DEFAULTS };
      const parsed = JSON.parse(raw);
      return { ...DEFAULTS, ...parsed };
    } catch {
      return { ...DEFAULTS };
    }
  });

  // Re-read when groupId changes (admin navigated to a different group).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(storageKey(groupId));
      if (raw) {
        const parsed = JSON.parse(raw);
        setViewState({ ...DEFAULTS, ...parsed });
      } else {
        setViewState({ ...DEFAULTS });
      }
    } catch {
      setViewState({ ...DEFAULTS });
    }
  }, [groupId]);

  // Persist on every change.
  const updateView = useCallback((patch) => {
    setViewState((prev) => {
      const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(storageKey(groupId), JSON.stringify(next));
        } catch {
          /* localStorage full / unavailable — ignore */
        }
      }
      return next;
    });
  }, [groupId]);

  const resetView = useCallback(() => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(storageKey(groupId));
      } catch {
        /* ignore */
      }
    }
    setViewState({ ...DEFAULTS });
  }, [groupId]);

  return { view, updateView, resetView };
}
